import { InjectionToken } from '@angular/core'

import type { MediaLibraryNominalApi } from '@flowx/designer/shared/api/media-library-nominal.api'
import type { MediaLibraryApi } from '@flowx/designer/shared/api/media-library.api'

import { AuditUser } from './versioning.dictionary'

export interface MediaLibraryResource {
  createdDate: string
  modifiedDate: string
  createdBy: string
  modifiedBy: AuditUser
  id: string
  key: string
  description?: string | null
  descriptionBody?: string | null
  application: string
  filename: string
  format: MediaLibraryFormat
  size: number
  storagePath: string
  thumbnailStoragePath: string
  tags: string[]
  appId: string
  resDefId: string
  contentType?: string
}

export type MediaAppInfo = {
  appId: string
  appName: string
}[]

export type PrivateDocument = {
  id: string
  key: string
  appId: string

  filename: string
  format: string
  contentType: string
  size: number

  createdDate: string
  createdBy: { firstName: string; lastName: string; username: string }
  modifiedDate: string
  modifiedBy: { firstName: string; lastName: string; username: string }

  tags: string[]
  systemResource: boolean
  storagePath: string
  thumbnailStoragePath: string
}

export const ICON_TAG = 'icon' // in the future we'll (probably) add more tags and better manage them, but for now thi should be enough

export enum FileFormat {
  DOCUMENT = 'document',
  IMAGE = 'image',
}

export enum MediaLibraryFormat {
  PNG = 'png',
  JPEG = 'jpeg',
  JPG = 'jpg',
  GIF = 'gif',
  SVG = 'svg',
  SVG_XML = 'svg+xml',
  WEBP = 'webp',
  PDF = 'pdf',
}

export const FileFormatMap = {
  [MediaLibraryFormat.PNG]: FileFormat.IMAGE,
  [MediaLibraryFormat.JPEG]: FileFormat.IMAGE,
  [MediaLibraryFormat.JPG]: FileFormat.IMAGE,
  [MediaLibraryFormat.GIF]: FileFormat.IMAGE,
  [MediaLibraryFormat.SVG]: FileFormat.IMAGE,
  [MediaLibraryFormat.SVG_XML]: FileFormat.IMAGE,
  [MediaLibraryFormat.WEBP]: FileFormat.IMAGE,
  [MediaLibraryFormat.PDF]: FileFormat.DOCUMENT,
}

export const ACCEPTED_FILE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/svg',
  'image/svg+xml',
  'image/webp',
  'application/pdf',
]

export const convertSizeToBytes = (sizeStr: string): number => {
  if (!sizeStr) {
    throw new Error('Size string must not be empty')
  }

  const size = parseFloat(sizeStr)
  if (isNaN(size)) {
    throw new Error('Invalid size format: must start with a number')
  }

  const normalizedStr = sizeStr.toLowerCase()
  if (normalizedStr.includes('gb')) {
    return size * 1024 * 1024 * 1024
  }
  if (normalizedStr.includes('mb')) {
    return size * 1024 * 1024
  }
  if (normalizedStr.includes('kb')) {
    return size * 1024
  }
  return size
}

export const getMaxFileSizeByType = (maxSize: string): Record<string, number> => {
  const maxSizeInBytes = convertSizeToBytes(maxSize)
  return {
    'image/png': maxSizeInBytes,
    'image/jpeg': maxSizeInBytes,
    'image/jpg': maxSizeInBytes,
    'image/gif': maxSizeInBytes,
    'image/svg': maxSizeInBytes,
    'image/svg+xml': maxSizeInBytes,
    'image/webp': maxSizeInBytes,
    'application/pdf': maxSizeInBytes,
  }
}

export interface MediaLibraryPayloadData {
  formData: FormData
  key: string
  tags: string[]
}

export const MEDIA_LIBRARY_API = new InjectionToken<MediaLibraryApi | MediaLibraryNominalApi>(
  'MEDIA_LIBRARY_API'
)
